<template>
  <div class="deal-email-reply">
    <div v-if="recipient && !threadId" class="deal-email-reply__recipient">
      <span class="deal-email-reply__recipient-label">{{ $t('deals.mail_to') }}</span>
      <span class="deal-email-reply__recipient-value">{{ recipient }}</span>
    </div>
    <TextInput
      id="deal-email-subject"
      :label="$t('deals.mail_subject')"
      v-model="subject"
    />
    <WysiwygEditor
      id="deal-email-body"
      :label="$t('deals.mail_body')"
      v-model="body"
    />
    <div class="deal-email-reply__actions">
      <Btn variant="outlined" @click="handleCancel">
        {{ $t('common.cancel') }}
      </Btn>
      <Btn
        variant="primary"
        icon="mail"
        :loading="sending"
        :disabled="!canSend"
        @click="handleSend"
      >
        {{ $t('deals.mail_send') }}
      </Btn>
    </div>
  </div>
</template>

<script>
  import { ref, computed, getCurrentInstance } from 'vue';
  import { useUIStore } from '@/store/uiStore';
  import { useToastStore } from '@/store/toastStore';
  import { dealEmailService } from '@/services/dealEmailService';
  import TextInput from '@/components/inputs/TextInput.vue';
  import WysiwygEditor from '@/components/inputs/WysiwygEditor.vue';
  import Btn from '@/components/partials/Btn.vue';

  export default {
    name: 'DealEmailReplyModal',

    components: {
      TextInput,
      WysiwygEditor,
      Btn,
    },

    props: {
      modalId: { type: [Number, String], default: null },
      dealId: { type: String, default: null },
      threadId: { type: String, default: null },
      initialSubject: { type: String, default: '' },
      recipient: { type: String, default: null },
      onSent: { type: Function, default: null },
    },

    setup(props) {
      const instance = getCurrentInstance();
      const $t = instance.appContext.config.globalProperties.$t;
      const uiStore = useUIStore();
      const toastStore = useToastStore();

      const subject = ref(props.initialSubject || '');
      const body = ref('');
      const sending = ref(false);

      const canSend = computed(
        () => !!subject.value.trim() && !!String(body.value || '').replace(/<[^>]+>/g, '').trim()
      );

      const close = () => {
        if (props.modalId) uiStore.closeModal(props.modalId);
      };

      const handleCancel = () => close();

      const handleSend = async () => {
        if (!canSend.value || sending.value) return;
        sending.value = true;
        try {
          if (props.threadId) {
            await dealEmailService.replyOnThread(props.threadId, {
              subject: subject.value.trim(),
              body: body.value,
            });
          } else if (props.dealId) {
            await dealEmailService.sendDealEmail(props.dealId, {
              subject: subject.value.trim(),
              body: body.value,
              recipients: props.recipient ? [props.recipient] : undefined,
            });
          } else {
            throw new Error('Missing thread or deal');
          }
          toastStore.addToast({ type: 'success', message: $t('deals.mail_sent') });
          if (props.onSent) await props.onSent();
          close();
        } catch (error) {
          toastStore.addToast({
            type: 'error',
            message: error.response?.data?.error || $t('deals.mail_send_failed'),
          });
        } finally {
          sending.value = false;
        }
      };

      return {
        subject,
        body,
        sending,
        canSend,
        handleCancel,
        handleSend,
      };
    },
  };
</script>

<style scoped lang="scss">
  .deal-email-reply {
    display: flex;
    flex-direction: column;
    gap: 1rem;

    &__recipient {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      padding: 0.5rem 0.75rem;
      border: 1px solid #e3e6ee;
      border-radius: 4px;
      background: #f6f7fa;
      font-size: 13px;
    }

    &__recipient-label {
      color: #434f6f;
    }

    &__recipient-value {
      color: #000921;
      font-weight: 500;
      @include ellipsis(1);
    }

    &__actions {
      display: flex;
      justify-content: flex-end;
      gap: 0.5rem;
    }
  }
</style>
